import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  clearError,
  clearOTPState,
} from "@/store/slices/auth.slice";
import { PhoneInput } from "@/components/auth/PhoneInput";
import { StandardCheckbox } from "@/components/common/StandardCheckbox";
import { ButtonPrimary } from "@/components/common/StandardButton";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface SignInFormData {
  phoneNumber: string;
  contactMethod: "mobile" | "whatsapp";
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const {
    isLoading,
    error,
    isOTPSent,
    phoneNumber: reduxPhoneNumber,
  } = useAppSelector((state) => state.auth);

  const { isAuthenticated, sendOtp } = useAuth("login");

  const referrerId = searchParams.get("referrer");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [contactMethod, setContactMethod] = useState<"mobile" | "whatsapp">(
    "whatsapp",
  );

  const {
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<SignInFormData>();

  // IMPORTANT: Clear stale OTP state on mount BEFORE any auth checks
  useEffect(() => {
    if (isOTPSent || reduxPhoneNumber || isLoading) {
      dispatch(clearOTPState());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Clear errors on mount
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearError());
    }
  }, [dispatch, isAuthenticated]);

  const validatePhoneNumber = (phone: string) => {
    const cleanPhone = phone.replace(/\s/g, "");
    return cleanPhone.length === 10 && /^\d{10}$/.test(cleanPhone);
  };

  const onSubmit = async () => {
    clearErrors();

    if (!phoneNumber.trim()) {
      setError("phoneNumber", { message: "Phone number is required" });
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError("phoneNumber", {
        message: "Please enter a valid 10-digit phone number",
      });
      return;
    }

    const otpPayload = referrerId
      ? {
          phoneNumber,
          inviteId: referrerId,
          channel: contactMethod === "whatsapp" ? "whatsapp" : "sms",
        }
      : {
          phoneNumber,
          channel: contactMethod === "whatsapp" ? "whatsapp" : "sms",
        };

    // Dispatch OTP request - navigation will happen automatically via useEffect
    // when isOTPSent becomes true (see lines 101-124)
    sendOtp(otpPayload);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <img
          src="/assets/auth/logo.png"
          alt="Shiphouse Logo"
          width={90}
          height={90}
        />
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-auth-text">
            {referrerId ? "Join via Invite! 🎉" : "Welcome to Shiphouse!👋"}
          </h1>
          <p className="text-base text-slate-600 dark:text-auth-text mt-2">
            {referrerId
              ? "You've been invited to join Shiphouse. Enter your details to get started."
              : "Log in to your account to start planning your next shoot."}
          </p>
          {referrerId && (
            <div className="mt-3 p-2 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-xs text-slate-600 dark:text-auth-text-muted text-center">
                Invite ID:{" "}
                <span className="text-primary font-mono">{referrerId}</span>
              </p>
            </div>
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex flex-col gap-4"
      >
        <div>
          <label className="text-sm text-slate-700 dark:text-auth-text mb-2 block">
            Mobile No.
          </label>
          <div className="flex items-center bg-white dark:bg-auth-card border border-slate-300 dark:border-auth-border rounded-full p-2 gap-2">
            <div className="flex items-center gap-2 text-white bg-primary px-4 py-2 rounded-full">
              <span className="text-white">+91</span>
              <ChevronDown size={16} className="text-white" />
            </div>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              placeholder="0000 0000 00"
              error={errors.phoneNumber?.message || error || undefined}
              className="bg-transparent text-slate-900 dark:text-auth-text placeholder-slate-400 dark:placeholder-auth-text-muted focus:outline-none w-full"
            />
          </div>
          {errors.phoneNumber && (
            <p className="text-red-500 text-xs mt-2">
              {errors.phoneNumber.message}
            </p>
          )}
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
        </div>

        <div className="flex justify-between items-center text-sm text-slate-700 dark:text-auth-text">
          <StandardCheckbox
            id="mobile-no"
            label="Mobile No"
            checked={contactMethod === "mobile"}
            onCheckedChange={() => setContactMethod("mobile")}
            labelClassName="text-slate-700 dark:text-auth-text"
          />
          <StandardCheckbox
            id="whatsapp"
            label="WhatsApp"
            checked={contactMethod === "whatsapp"}
            onCheckedChange={() => setContactMethod("whatsapp")}
            labelClassName="text-slate-700 dark:text-auth-text"
          />
        </div>

        <ButtonPrimary
          type="submit"
          disabled={isLoading}
          size="lg"
          shape="pill"
          fullWidth
          className="font-semibold text-lg"
        >
          {isLoading ? "Sending..." : "Send OTP"}
        </ButtonPrimary>
      </form>
    </div>
  );
}
